import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Vg2Shell } from "./Vg2Shell";
import {
  generateActionPlan,
  getAgisciOpId,
  agisciOpIdForDecision,
} from "@/lib/vg2Agisci";
import { getDecidiChecklist, getDecision } from "@/lib/vg2Decidi";
import { getPaConsentCopy, submitPaRequest, getPaRequestForCase, getPaReferralInfo } from "@/lib/vg2PaRequest";
import { useAuth } from "@/lib/AuthContext";
import { useOwnerSignupPanel } from "@/components/vg2/OwnerSignupPanel";

/**
 * AGISCI page — Phase 6.
 * Renders the deterministic ActionPlan derived from the current OwnerDecision.
 * Never generates on GET. No provider matching. No Circle. No EVA chat.
 */
export default function Agisci() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [phase, setPhase] = React.useState("LOADING");
  const [view, setView] = React.useState(null);
  const [scenarioTitle, setScenarioTitle] = React.useState(null);
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    setPhase("LOADING");
    setError(null);
    try {
      // §LAUNCH-B3 · Leggi la scelta corrente in DECIDI così il piano segue
      // sempre lo scenario/versione scelti (evita il piano "incollato" al
      // primo scenario). L'op-id deterministico cambia quando cambia la
      // scelta o la versione di DECIDI → il backend ricostruisce il piano
      // dallo stato corrente; altrimenti restituisce quello già calcolato.
      let opId = getAgisciOpId(caseId, "current"); // fallback (nessuna scelta)
      try {
        const dec = await getDecision(caseId);
        const od = (dec && dec.owner_decision) || {};
        if (od.selected_scenario_key) {
          opId = agisciOpIdForDecision(
            caseId, od.selected_scenario_key,
            dec.decision_version, od.choice_version,
          );
          setScenarioTitle(od.selected_scenario_title || od.selected_scenario_key);
        }
      } catch { /* nessuna decisione ancora → generate restituirà 409 */ }

      const v = await generateActionPlan(caseId, opId);
      setView(v);
      setPhase("READY");
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.detail?.error_code;
      if (status === 409 && code === "AGISCI_MISSING_CONTEXT") {
        setPhase("MISSING_CONTEXT");
      } else if (status === 409 && code === "DECISION_SUPERSEDED") {
        setPhase("STALE_DECISION");
      } else if (status === 404) {
        setError("Caso non trovato o non autorizzato.");
        setPhase("ERROR");
      } else {
        setError("Non riesco a preparare il piano in questo momento.");
        setPhase("ERROR");
      }
    }
  }, [caseId]);

  React.useEffect(() => { load(); }, [load]);

  if (phase === "LOADING") return (
    <Vg2Shell>
      <div className="pt-24 text-vg2-muted" data-testid="agisci-loading">
        Sto preparando il tuo percorso operativo…
      </div>
    </Vg2Shell>
  );
  if (phase === "MISSING_CONTEXT") return (
    <Vg2Shell>
      <div className="pt-24" data-testid="agisci-missing-context">
        <h1 className="font-display font-semibold text-3xl mb-4">
          Prima serve una decisione
        </h1>
        <p className="text-vg2-muted mb-8 max-w-[600px]">
          Vai in DECIDI, conferma o scegli una direzione, poi torna qui per il piano.
        </p>
        <button
          onClick={() => navigate(`/vg2/decidi/${caseId}`)}
          className="px-5 py-3 bg-vg2-red text-white rounded hover:bg-vg2-red-hover"
          data-testid="agisci-goto-decidi"
        >
          Torna a DECIDI
        </button>
      </div>
    </Vg2Shell>
  );
  if (phase === "STALE_DECISION") return (
    <Vg2Shell>
      <div className="pt-24" data-testid="agisci-stale-decision">
        <h1 className="font-display font-semibold text-3xl mb-4">
          L’analisi è stata aggiornata
        </h1>
        <p className="text-vg2-muted mb-8 max-w-[600px]">
          La tua scelta precedente è conservata nello storico, ma si
          riferisce a una versione superata dell’analisi. Torna a DECIDI e
          conferma una nuova scelta: il piano verrà generato sulla versione
          corrente.
        </p>
        <button
          onClick={() => navigate(`/vg2/decidi/${caseId}`)}
          className="px-5 py-3 bg-vg2-red text-white rounded hover:bg-vg2-red-hover"
          data-testid="agisci-stale-goto-decidi"
        >
          Torna a DECIDI
        </button>
      </div>
    </Vg2Shell>
  );
  if (phase === "ERROR") return (
    <Vg2Shell>
      <div className="pt-24" data-testid="agisci-error">
        <p role="alert" className="text-vg2-error mb-6">{error}</p>
        <button onClick={load} data-testid="agisci-retry"
                className="px-4 py-2 border border-vg2-line rounded hover:bg-vg2-subtle">
          Riprova
        </button>
      </div>
    </Vg2Shell>
  );
  return <Vg2Shell><AgisciPage view={view} caseId={caseId} scenarioTitle={scenarioTitle} onBackToDecidi={() => navigate(`/vg2/decidi/${caseId}`)} onOpenEva={() => navigate(`/vg2/eva/${caseId}`)} /></Vg2Shell>;
}

const SCENARIO_LABELS_IT = {
  SELL_NOW: "Vendere nello stato attuale",
  LONG_TERM_RENT: "Mettere a reddito (canone a lungo termine)",
  SHORT_TERM_RENT: "Locazione turistica / breve periodo",
  RENOVATE_THEN_SHORT_TERM_RENT: "Lavori e poi locazione turistica / breve periodo",
  RENOVATE_SELL: "Ristrutturare e vendere",
  RENOVATE_RENT: "Ristrutturare e mettere a reddito",
  KEEP_AS_IS: "Tenere l'immobile",
  SPLIT_SELL_AND_RENT: "Frazionare: vendere un'unità e affittare l'altra",
};

function RequestRefBanner({ ref_ }) {
  if (!ref_?.selected) return null;
  const s = ref_.selected;
  return (
    <div className="rounded-xl border border-vg2-line bg-vg2-subtle px-4 py-3 mb-6 max-w-[720px]" data-testid="agisci-request-ref">
      <p className="text-sm" data-testid="agisci-request-ref-origin">
        {s.origin === "requested"
          ? <>Operazione <strong>che hai chiesto</strong>{s.label ? <> («{s.label}»)</> : null}</>
          : <>Alternativa <strong>suggerita da ValutaGenio</strong> (non era tra le tue richieste)</>}
        {" "}· versione {ref_.request_version} della richiesta
      </p>
      {s.feasibility === "vincolo" && (
        <ul className="mt-2 text-xs text-amber-800 list-disc pl-4 space-y-0.5" data-testid="agisci-request-ref-constraint">
          <li>Prima di ogni altro passo va superato il vincolo indicato in DECIDI.</li>
          {(s.feasibility_notes || []).map((n) => <li key={n}>{n}</li>)}
        </ul>
      )}
    </div>
  );
}


function AgisciPage({ view, caseId, scenarioTitle, onBackToDecidi, onOpenEva }) {
  const planScenarioLabel =
    scenarioTitle
    || SCENARIO_LABELS_IT[view.selected_scenario_key]
    || view.selected_scenario_key;
  return (
    <div className="pt-12 pb-24" data-testid={`agisci-page-${view.status.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-4"
         data-testid="agisci-stage-label">Agisci · Percorso operativo</p>

      {planScenarioLabel && (
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full border border-vg2-red/30 bg-vg2-subtle"
             data-testid="agisci-scenario-badge">
          <span className="text-[11px] uppercase tracking-[0.14em] text-vg2-muted">Piano per lo scenario</span>
          <span className="text-sm font-medium text-vg2-ink" data-testid="agisci-scenario-name">
            {planScenarioLabel}
          </span>
        </div>
      )}

      <RequestRefBanner ref_={view.structured_request_ref} />

      <h1 className="font-display font-semibold text-3xl md:text-4xl leading-tight mb-3"
          data-testid="agisci-headline">
        {view.status_label}
      </h1>
      <p className="text-vg2-ink text-lg leading-relaxed max-w-[640px] mb-4"
         data-testid="agisci-objective">
        {view.objective_summary}
      </p>
      {view.current_position && (
        <p className="text-vg2-muted max-w-[640px] mb-8" data-testid="agisci-position">
          {view.current_position}
        </p>
      )}

      {view.status === "NOT_READY" && (
        <NotReadyPanel view={view} onBackToDecidi={onBackToDecidi} />
      )}

      {view.next_action && (
        <NextActionPanel action={view.next_action} />
      )}

      {/* M8.5 · Ricevi l'analisi via email — promosso a CTA primario:
          è il vero obiettivo finale del percorso AGISCI e va in alto,
          subito dopo il prossimo passo, con il massimo risalto. */}
      {["READY", "PARTIAL", "PREREQUISITE_FIRST"].includes(view.status) && (
        <FinalEmailSection caseId={caseId} primary />
      )}

      {["READY", "PARTIAL", "PREREQUISITE_FIRST"].includes(view.status) && (
        <EvaCtaPanel onOpen={onOpenEva} />
      )}

      {view.prerequisites?.length > 0 && (
        <PrerequisiteSection prerequisites={view.prerequisites}
                             summary={view.prerequisite_summary} />
      )}

      {/* M8.5 dedupe · ActionTimeline rimossa: duplicava
          NextActionPanel (prossimo passo) + MilestonesSection (tappe) +
          DocumentsSection (documenti) + RolesSection (professionisti).
          Inoltre attribuiva erroneamente al Property Advisor l'azione
          tecnica di verifica idoneità (correttamente in carico al
          Geometra secondo RolesSection). */}

      {view.milestones?.length > 0 && (
        <MilestonesSection milestones={view.milestones} />
      )}

      {/* Post-collaudo · in primo piano solo la prossima tappa: il resto del piano su richiesta. */}
      <MoreOfPlan>
        <OutstandingChecklistSection caseId={caseId} />

        {view.requirements?.length > 0 && (
          <RequirementsSection items={view.requirements} />
        )}

        {view.professional_roles?.length > 0 && (
          <RolesSection items={view.professional_roles} />
        )}

        {view.open_questions?.length > 0 && (
          <OpenQuestionsSection items={view.open_questions} />
        )}
      </MoreOfPlan>

      {/* M8.5 dedupe · AgisciInfographics ("Il quadro operativo",
          "Stato del caso", "Le fasi del percorso" [seconda occorrenza],
          "Priorità", "Chi può aiutarti") rimossa: placeholder vuoti
          ("Documento"/"Requisito") e comunque ridondante con le sezioni
          sopra. DossierSection ("Genera dossier proprietario") rimossa:
          bottone non funzionante e fuori scope. */}

      {["READY", "PARTIAL", "PREREQUISITE_FIRST"].includes(view.status) && (
        <PropertyAdvisorSection caseId={caseId} view={view} />
      )}

      <AgisciSignupNudge />
    </div>
  );
}


// §M8.7 · Nudge di registrazione a fine percorso AGISCI. Nascosto se
// l'utente è già loggato come owner (il salvataggio avviene già in
// automatico sul suo profilo). Testo semplice, single CTA che apre il
// pannello di iscrizione responsive slide-in / bottom-sheet.
function AgisciSignupNudge() {
  const { user } = useAuth();
  const { openPanel, Panel } = useOwnerSignupPanel();
  if (user?.ruolo === "owner") return null;
  return (
    <>
      <section
        className="mt-14 mb-6 border-t border-vg2-line pt-8"
        data-testid="agisci-signup-nudge"
      >
        <p className="text-xs uppercase tracking-[0.14em] text-vg2-muted mb-3">
          Vuoi tenere questa analisi con te?
        </p>
        <h2 className="font-display text-2xl leading-tight mb-3">
          Registrati in pochi secondi e salva il tuo caso.
        </h2>
        <p className="text-vg2-ink leading-relaxed max-w-[560px] mb-5">
          Un profilo gratuito ti permette di riaprire questa analisi da
          qualsiasi dispositivo, modificare i dati dell'immobile e
          rivedere la stima nel tempo.
        </p>
        <button
          type="button"
          onClick={openPanel}
          data-testid="agisci-signup-nudge-cta"
          className="
            inline-flex items-center gap-2 bg-[#B40101] hover:bg-[#8F0101]
            text-white font-semibold text-base px-5 py-3 rounded-xl
            transition-colors
          "
        >
          Salva questa analisi
        </button>
      </section>
      <Panel />
    </>
  );
}


function PropertyAdvisorSection({ caseId, view }) {
  const [copy, setCopy] = React.useState(null);
  const [existing, setExisting] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", email: "", consent: false, preferDifferent: false });
  const [referral, setReferral] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [confirmed, setConfirmed] = React.useState(false);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    Promise.allSettled([getPaConsentCopy(), getPaRequestForCase(caseId), getPaReferralInfo(caseId)])
      .then(([c, r, ri]) => {
        if (!alive) return;
        if (c.status === "fulfilled") setCopy(c.value);
        if (r.status === "fulfilled" && r.value?.exists) setExisting(r.value);
        if (ri.status === "fulfilled" && ri.value?.has_referral) setReferral(ri.value);
        setReady(true);
      });
    return () => { alive = false; };
  }, [caseId]);

  const revokedBefore = existing?.consent_status === "REVOKED" && !confirmed;
  const alreadyAsked = !!existing && !revokedBefore;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null); setBusy(true);
    try {
      const body = {
        case_id: caseId,
        owner_name: form.name.trim(),
        owner_email: form.email.trim(),
        consent: !!form.consent,
        consent_version: copy?.consent_version || "vg2_pa_request_v1",
        operation_id: `pa-req:${caseId}:${view?.action_plan_version || 1}`,
        prefer_different_pa: !!(referral && form.preferDifferent),
      };
      const r = await submitPaRequest(body);
      setExisting({ ...r, consent_status: "ACTIVE" });
      setConfirmed(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail?.message || "Non riesco a inviare la richiesta. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mb-10 border-t border-vg2-line pt-8"
             data-testid="agisci-pa">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
        <div className="md:col-span-6">
          <img
            src="/images/vg2/vg-agisci-property-advisor.webp"
            alt="Property Advisor e proprietario al tavolo che guardano insieme il dossier con grafici, scenari e timeline."
            width={1448} height={1086}
            loading="lazy" decoding="async"
            data-testid="agisci-pa-photo"
            className="w-full aspect-[4/3] object-cover rounded-2xl shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)]"
          />
        </div>
        <div className="md:col-span-6">
          <h2 className="font-display text-xl font-medium mb-3">
            Parla con un Property Advisor
          </h2>
          <p className="text-sm text-vg2-ink leading-relaxed mb-3">
            <strong>ValutaGenio mette il proprietario al centro delle
            decisioni immobiliari. E non lo lascia solo.</strong> Il
            Property Advisor ti aiuta a leggere il quadro, verificare i
            passaggi decisivi e portare la direzione scelta dentro la
            realtà.
          </p>
          <p className="text-xs text-vg2-muted leading-relaxed mb-5">
            Il confronto parte soltanto da una tua richiesta. Non
            comporta incarichi o impegni commerciali. Eventuali servizi
            successivi saranno valutati e accettati da te, in modo
            esplicito.
          </p>
          {alreadyAsked || confirmed ? (
            <div data-testid="agisci-pa-confirmed"
                 className="p-4 rounded-lg border border-green-200 bg-green-50">
              <p className="font-medium text-green-900">Richiesta ricevuta</p>
              <p className="text-sm text-green-800 mt-1">
                Un Property Advisor potrà leggere il quadro che hai
                costruito e partire dalla direzione scelta. Non dovrai
                raccontare tutto da capo. Il confronto non comporta
                incarichi o impegni commerciali.
              </p>
            </div>
          ) : open ? (
            <form onSubmit={submit} className="space-y-3"
                  data-testid="agisci-pa-form">
              {revokedBefore && <RevokedNotice />}
              <label className="block">
                <span className="text-sm font-medium">Nome e cognome</span>
                <input type="text" required
                       value={form.name}
                       onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                       data-testid="agisci-pa-name"
                       className="mt-1 w-full border border-vg2-line rounded px-3 py-2 focus:outline-none focus:border-vg2-red" />
              </label>
              <label className="block">
                <span className="text-sm font-medium">Email</span>
                <input type="email" required
                       value={form.email}
                       onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                       data-testid="agisci-pa-email"
                       className="mt-1 w-full border border-vg2-line rounded px-3 py-2 focus:outline-none focus:border-vg2-red" />
              </label>
              {referral && <ReferralNotice referral={referral} checked={form.preferDifferent}
                onChange={(v) => setForm(f => ({ ...f, preferDifferent: v }))} />}
              {copy?.consent_text && (
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox"
                         checked={form.consent}
                         onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                         data-testid="agisci-pa-consent"
                         className="mt-1" required />
                  <span className="text-xs text-vg2-muted leading-relaxed">
                    {copy.consent_text}
                  </span>
                </label>
              )}
              {error && (
                <p role="alert" className="text-xs text-vg2-error"
                   data-testid="agisci-pa-error">{error}</p>
              )}
              <div className="flex items-center gap-3">
                <button type="submit" disabled={busy}
                        data-testid="agisci-pa-submit"
                        className="px-5 py-3 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover font-semibold disabled:opacity-60">
                  {busy ? "Invio…" : "Invia la richiesta"}
                </button>
                <button type="button" onClick={() => setOpen(false)}
                        className="text-sm text-vg2-muted underline">
                  Annulla
                </button>
              </div>
            </form>
          ) : !ready ? (
            <p className="text-sm text-vg2-muted" data-testid="agisci-pa-loading">Caricamento…</p>
          ) : (
            <div className="space-y-3">
              {revokedBefore && <RevokedNotice />}
              <button type="button" onClick={() => setOpen(true)}
                      data-testid="agisci-pa-cta"
                      className="inline-flex items-center gap-2 px-5 py-3 bg-vg2-red text-white rounded-full hover:bg-vg2-red-hover font-semibold">
                {revokedBefore ? "Presenta una nuova richiesta →" : "Chiedi il confronto gratuito con un Property Advisor →"}
              </button>
            </div>
          )}
          <p className="text-xs text-vg2-muted mt-4 max-w-[560px]">
            Hai costruito il quadro. Ora puoi trasformarlo in un piano
            verificato, senza dover raccontare tutto da capo.
          </p>
        </div>
      </div>
    </section>
  );
}


function RevokedNotice() {
  return (
    <div className="p-4 rounded-lg border border-amber-200 bg-amber-50" data-testid="agisci-pa-revoked">
      <p className="font-medium text-amber-900">Consenso revocato</p>
      <p className="text-sm text-amber-800 mt-1">
        Hai revocato il consenso: nessun Property Advisor può vedere il caso né i tuoi
        recapiti. Se vuoi, puoi presentare una nuova richiesta con un nuovo consenso.
      </p>
    </div>
  );
}

function ReferralNotice({ referral, checked, onChange }) {
  return (
    <div className="p-3 rounded-lg border border-vg2-line text-sm" data-testid="agisci-pa-referral">
      <p data-testid="agisci-pa-referral-name">
        Sei arrivato su ValutaGenio tramite il link di <strong>{referral.pa_name}</strong>.{" "}
        {referral.pa_available
          ? "Di norma la tua richiesta sarà seguita da questo professionista."
          : "Il professionista non è al momento disponibile: la richiesta sarà affidata a un altro Property Advisor."}
      </p>
      {referral.pa_available && (
        <label className="flex items-start gap-2 mt-2">
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
                 data-testid="agisci-pa-prefer-different" className="mt-1" />
          <span className="text-xs text-vg2-muted">Preferisco essere seguito da un professionista diverso.</span>
        </label>
      )}
    </div>
  );
}

function NotReadyPanel({ view, onBackToDecidi }) {
  return (
    <div className="border border-vg2-line rounded p-6 bg-vg2-subtle mb-8"
         data-testid="agisci-not-ready">
      <p className="font-medium mb-2">{view.not_ready_reason_label}</p>
      <p className="text-sm text-vg2-muted mb-4">
        Il piano operativo si prepara insieme alla direzione scelta.
      </p>
      <button onClick={onBackToDecidi}
              className="px-4 py-2 border border-vg2-red text-vg2-red rounded hover:bg-vg2-red hover:text-white"
              data-testid="agisci-back-to-decidi">
        Torna a DECIDI
      </button>
    </div>
  );
}


function NextActionPanel({ action }) {
  return (
    <section className="mb-10 border-l-4 border-vg2-red bg-vg2-subtle p-5 rounded-r"
             data-testid="agisci-next-action">
      <p className="text-xs uppercase tracking-[0.14em] text-vg2-red mb-1">Prossimo passo</p>
      <p className="font-display text-lg font-medium mb-1">{action.title}</p>
      <p className="text-sm text-vg2-ink leading-relaxed">{action.description}</p>
    </section>
  );
}


function EvaCtaPanel({ onOpen }) {
  return (
    <section className="mb-10" data-testid="agisci-eva-cta">
      <div className="flex items-center justify-between border border-vg2-line rounded p-5">
        <div className="pr-4">
          <p className="font-display text-base font-medium mb-1">
            Chiarisci gli ultimi punti con EVA
          </p>
          <p className="text-sm text-vg2-muted">
            Una breve conversazione per fissare cosa resta da chiarire e come procedere.
          </p>
        </div>
        <button onClick={onOpen}
                className="px-4 py-2 bg-vg2-red text-white rounded hover:opacity-90 whitespace-nowrap"
                data-testid="agisci-open-eva-btn">
          Parla con EVA
        </button>
      </div>
    </section>
  );
}


function PrerequisiteSection({ prerequisites, summary }) {
  return (
    <section className="mb-10" data-testid="agisci-prerequisites">
      <h2 className="font-display text-lg font-medium mb-3">Cosa va risolto prima</h2>
      {summary && (
        <p className="text-sm text-vg2-muted mb-4">{summary}</p>
      )}
      {prerequisites.map((m) => (<MilestoneCard key={m.milestone_id} m={m} isPrereq />))}
    </section>
  );
}


function MoreOfPlan({ children }) {
  const [open, setOpen] = React.useState(false);
  return (
    <section className="mb-10" data-testid="agisci-more">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="agisci-more-toggle"
              className="text-sm underline text-vg2-ink">
        {open ? "Nascondi documenti, requisiti e professionisti" : "Mostra documenti, requisiti e professionisti di tutto il percorso"}
      </button>
      {open && <div className="mt-6">{children}</div>}
    </section>
  );
}

function MilestonesSection({ milestones }) {
  return (
    <section className="mb-10" data-testid="agisci-milestones">
      <h2 className="font-display text-lg font-medium mb-3">Il percorso</h2>
      <p className="text-sm text-vg2-muted mb-3">Parti dalla prima tappa: le successive si aprono quando ti servono.</p>
      <div className="space-y-4">
        {milestones.map((m, i) => (<MilestoneCard key={m.milestone_id} m={m} initiallyOpen={i === 0} />))}
      </div>
    </section>
  );
}


function MilestoneCard({ m, isPrereq = false, initiallyOpen = false }) {
  const [expanded, setExpanded] = React.useState(initiallyOpen);
  return (
    <article className={`border rounded-lg p-5 ${isPrereq ? "border-amber-300 bg-amber-50/40" : "border-vg2-line bg-vg2-canvas"}`}
             data-testid={`agisci-milestone-${m.sequence}`}>
      <header className="flex items-start justify-between mb-2 gap-3">
        <div>
          <p className="text-xs text-vg2-muted mb-1">Tappa {m.sequence}</p>
          <h3 className="font-display text-lg font-medium leading-tight">{m.title}</h3>
        </div>
        <span className="text-[11px] uppercase tracking-wide text-vg2-muted border border-vg2-line rounded px-2 py-0.5 shrink-0">
          {m.status_label}
        </span>
      </header>
      <p className="text-sm text-vg2-ink leading-relaxed mb-3">{m.purpose}</p>
      <button type="button" onClick={() => setExpanded((e) => !e)}
              className="text-xs uppercase tracking-[0.14em] text-vg2-muted hover:text-vg2-ink"
              aria-expanded={expanded}
              data-testid="agisci-milestone-toggle">
        {expanded ? "Nascondi azioni" : "Mostra azioni"}
      </button>
      {expanded && (
        <ul className="mt-3 space-y-3" data-testid="agisci-milestone-actions">
          {m.actions.map((a) => (
            <li key={a.action_id} className="border-l-2 border-vg2-line pl-3">
              <p className="text-sm font-medium">{a.sequence}. {a.title}</p>
              <p className="text-xs text-vg2-muted uppercase tracking-wide mb-1">
                {a.action_type_label}
              </p>
              <p className="text-xs text-vg2-ink mb-1" data-testid="agisci-action-role">
                Chi ti aiuta: <strong>{a.responsible_role_label || "lo fai tu, come proprietario"}</strong>
              </p>
              <p className="text-sm">{a.description}</p>
              {a.completion_condition && (
                <p className="text-xs text-vg2-muted mt-1">
                  Completata quando: {a.completion_condition}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}


function RequirementsSection({ items }) {
  return (
    <section className="mb-10" data-testid="agisci-requirements">
      <h2 className="font-display text-lg font-medium mb-3">Cosa serve</h2>
      <ul className="space-y-2 text-sm">
        {items.map((r) => (
          <li key={r.key} className="border border-vg2-line rounded p-3">
            <p className="font-medium">{r.label}
              <span className="ml-2 text-xs text-vg2-muted">— {r.status_label}</span>
            </p>
            <p className="text-vg2-muted">{r.why_it_matters}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}


// §M8.5 · Riporta dalla checklist DECIDI solo i documenti che il
// proprietario ha marcato come "Non ce l'ho" / "Non so" o che non ha
// ancora risposto. Se ha già risposto "Ce l'ho" per un item, questo
// non compare qui — evitiamo di ripetere la lista intera generica.
function OutstandingChecklistSection({ caseId }) {
  const [state, setState] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await getDecidiChecklist(caseId);
        if (!cancelled) setState(data);
      } catch { /* checklist not available yet */ }
      finally { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, [caseId]);

  if (!loaded || !state) return null;
  const outstanding = (state.items || []).filter(
    (it) => it.status !== "have"
  );
  if (outstanding.length === 0) {
    return (
      <section className="mb-10" data-testid="agisci-checklist-complete">
        <h2 className="font-display text-lg font-medium mb-2">
          Documenti e verifiche
        </h2>
        <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">
          Risulta che hai già in mano tutti i
          documenti richiesti per la direzione scelta. Bene così — puoi
          procedere con il prossimo passo.
        </p>
      </section>
    );
  }
  return (
    <section className="mb-10" data-testid="agisci-checklist-outstanding">
      <h2 className="font-display text-lg font-medium mb-1">
        Cosa ti resta da procurare o verificare
      </h2>
      <p className="text-sm text-vg2-muted mb-4">
        Ecco cosa ti resta da procurare o verificare per l'operazione scelta: ciò che hai già segnato come
        disponibile non compare.
      </p>
      <ul className="space-y-2 text-sm">
        {outstanding.map((it) => {
          const badge =
            it.status === "missing" ? { label: "Da procurare", cls: "bg-red-100 text-red-700" } :
            it.status === "unknown" ? { label: "Da verificare", cls: "bg-amber-100 text-amber-700" } :
                                       { label: "Da valutare", cls: "bg-neutral-100 text-neutral-700" };
          return (
            <li key={it.item_id}
                className="border border-vg2-line rounded p-3"
                data-testid={`agisci-outstanding-${it.item_id}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <p className="font-medium">{it.document}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full ${badge.cls}`}>
                  {badge.label}
                </span>
              </div>
              <p className="text-vg2-muted text-xs mt-1">
                <span className="uppercase tracking-wider">Chi</span>
                {" · "}{it.professional}
              </p>
              <p className="text-vg2-muted text-xs mt-0.5">
                <span className="uppercase tracking-wider">Dove</span>
                {" · "}{it.where}
              </p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}



function DocumentsSection({ items }) {
  return (
    <section className="mb-10" data-testid="agisci-documents">
      <h2 className="font-display text-lg font-medium mb-3">Documenti da preparare o verificare</h2>
      <ul className="space-y-2 text-sm">
        {items.map((d) => (
          <li key={d.key} className="border border-vg2-line rounded p-3">
            <p className="font-medium">{d.label}
              <span className="ml-2 text-xs text-vg2-muted">— {d.status_label}</span>
            </p>
            <p className="text-vg2-muted">{d.purpose}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}


function RolesSection({ items }) {
  return (
    <section className="mb-10" data-testid="agisci-roles">
      <h2 className="font-display text-lg font-medium mb-1">
        Servizi che ti serviranno
      </h2>
      <p className="text-sm text-vg2-muted mb-4">
        Queste sono le categorie di professionisti che il tuo caso richiede. In
        questa fase vedi solo i ruoli, non i nominativi.
      </p>
      <ul className="space-y-2 text-sm">
        {items.map((r) => (
          <li key={r.role_key}
              className="border border-vg2-line rounded p-3"
              data-testid={`agisci-role-${r.role_key}`}>
            <p className="font-medium">{r.role_label}</p>
            <p className="text-vg2-muted">{r.purpose}</p>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-xs text-vg2-muted italic border-l-2 border-vg2-red/30 pl-3"
         data-testid="agisci-roles-gate-copy">
        Se scegli di proseguire con il tuo Property Advisor dopo la call,
        potrai capire insieme a lui quali professionisti ti servono
        davvero e come reperirli — anche attraverso la sua rete, quando
        disponibile.
      </p>
    </section>
  );
}


function OpenQuestionsSection({ items }) {
  return (
    <section className="mb-10" data-testid="agisci-open-questions">
      <h2 className="font-display text-lg font-medium mb-3">Punti ancora da chiarire</h2>
      <ul className="space-y-2 text-sm">
        {items.map((q, i) => (
          <li key={i} className="border-l-2 border-vg2-line pl-3">
            <p className="font-medium">{q.topic}</p>
            <p className="text-vg2-muted">{q.why_it_matters}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}


// ------------------------------------------------------------------------
// §10 · Single final email — Owner-triggered send-once for the whole
// journey (CAPISCI + VALUTA + DECIDI + AGISCI in one message).
// ------------------------------------------------------------------------

function FinalEmailSection({ caseId, primary = false }) {
  const [state, setState] = React.useState("idle"); // idle|sending|sent|error
  const [message, setMessage] = React.useState(null);
  const inFlight = React.useRef(false);
  const onSend = async () => {
    if (inFlight.current || state === "sending" || state === "sent") return;
    inFlight.current = true;
    setState("sending"); setMessage(null);
    try {
      const { createAnonAxios } = await import("@/lib/vg2AnonAxios");
      const client = createAnonAxios(
        `${process.env.REACT_APP_BACKEND_URL}/api/v2`,
      );
      const opId = `final:${caseId}`;
      const r = await client.post("/emails/send", {
        case_id: caseId,
        template_id: "FINAL_JOURNEY",
        operation_id: opId,
      });
      const ds = r?.data?.delivery_status;
      if (ds === "SENT") {
        setState("sent");
        setMessage("Analisi completa inviata alla tua email.");
      } else if (ds === "PREVIEW_SUPPRESSED" || ds === "REQUIRES_CONFIGURATION") {
        setState("sent");
        setMessage("Richiesta di invio registrata. In preview l’email non viene consegnata realmente.");
      } else if (ds === "NOT_ELIGIBLE") {
        setState("error");
        setMessage("Completa prima la scelta e il piano d’azione per ricevere l’analisi.");
      } else {
        setState("sent");
        setMessage("Richiesta di invio registrata.");
      }
    } catch (e) {
      setState("error");
      setMessage(
        e?.response?.data?.detail?.message
          || e?.response?.data?.detail?.error_code
          || "Non è stato possibile inviare adesso. Riprova."
      );
    } finally {
      inFlight.current = false;
    }
  };

  // M8.5 · Primary variant — CTA finale del percorso, con la massima
  // enfasi visiva: bordo rosso, sfondo subtle, headline in serif large.
  // Compatta variant — nel raro caso in cui venga chiamata di nuovo in
  // fondo alla pagina, resta come promemoria discreto.
  if (primary) {
    return (
      <section
        className="mb-12 border-2 border-vg2-red rounded-xl bg-vg2-subtle p-6 md:p-8"
        data-testid="agisci-final-email-primary"
      >
        <p className="text-xs uppercase tracking-[0.18em] text-vg2-red font-semibold mb-3">
          Il traguardo del percorso
        </p>
        <h2 className="font-display font-semibold text-2xl md:text-3xl leading-tight text-vg2-ink mb-3">
          Ricevi l’analisi completa via email
        </h2>
        <p className="text-vg2-ink text-base leading-relaxed max-w-[620px] mb-6">
          Un solo messaggio con il quadro completo: obiettivo, valore
          attuale stimato, potenziale rispetto al tuo obiettivo, direzione
          scelta, piano d’azione e Property Advisor. È il documento che
          porterai con te alla call di orientamento.
        </p>
        <button
          type="button"
          onClick={onSend}
          disabled={state === "sending" || state === "sent"}
          data-testid="agisci-send-final-email"
          className={
            "inline-flex items-center gap-3 rounded-full px-7 py-3.5 text-base font-semibold transition-all "
            + (state === "sent"
              ? "bg-vg2-line text-vg2-muted cursor-default"
              : "bg-vg2-red text-white hover:bg-vg2-red-hover shadow-[0_10px_30px_rgba(180,1,1,0.28)] hover:-translate-y-0.5")
          }
        >
          {state === "sending" ? "Invio in corso…"
            : state === "sent" ? "Invio registrato ✓"
            : "Ricevi l’analisi completa via email →"}
        </button>
        {message && (
          <p className={"mt-4 text-sm "
            + (state === "error" ? "text-vg2-error" : "text-vg2-muted")}
             data-testid="agisci-final-email-status">
            {message}
          </p>
        )}
      </section>
    );
  }

  // M8.6 · Only the primary variant is used from AgisciPage; the
  // pre-M8.5 non-primary variant was removed as unreachable.
  return null;
}
